/**
 * <NotFound> — friendly 404 catch-all for any URL that doesn't match a registered route.
 *
 * Prevents the entire class of "blank screen because React Router silently
 * renders nothing on unmatched paths". Now any stray click, stale bookmark,
 * or typo URL lands on a real page with a way back.
 */
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Compass, ArrowLeft, Home } from 'lucide-react';

const NotFound = () => {
  const location = useLocation();
  const isManagerArea = location.pathname.startsWith('/manager');

  return (
    <div
      data-testid="not-found-page"
      className="min-h-screen w-full flex items-center justify-center bg-gradient-to-b from-blue-50 to-white p-6"
    >
      <div className="max-w-lg w-full bg-white rounded-2xl shadow-xl ring-1 ring-blue-100 p-8 text-center">
        <div className="w-14 h-14 mx-auto rounded-full bg-blue-50 ring-1 ring-blue-200 flex items-center justify-center mb-4">
          <Compass className="text-blue-600" size={26} />
        </div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Page not found</h1>
        <p className="text-gray-600 mb-1">We couldn't find</p>
        <p className="text-sm font-mono text-blue-700 bg-blue-50 ring-1 ring-blue-100 rounded-lg py-2 px-3 mb-6 break-all">
          {location.pathname}
        </p>
        <p className="text-gray-600 mb-6">
          The link might be old, mistyped, or the page has been moved. Head back to your dashboard
          and try again from there.
        </p>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to={isManagerArea ? '/manager/home' : '/'}
            data-testid="not-found-home"
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm transition-colors"
          >
            <Home size={16} /> {isManagerArea ? 'Go to dashboard' : 'Go to homepage'}
          </Link>
          <button
            type="button"
            onClick={() => window.history.back()}
            data-testid="not-found-back"
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-white ring-1 ring-gray-300 hover:ring-gray-400 text-gray-800 font-semibold text-sm transition-colors"
          >
            <ArrowLeft size={16} /> Go back
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotFound;
